import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import { Action, Chip, ErrorNote, Modal, Panel, PanelHead, SettingRow, Toggle } from '../aa';
import { billingApi, type RenewalSeat, type SeatRenewal } from '../../services/billingApi';

const money = (cents: number) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const day = (iso: string | null | undefined) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const roleLabel = (role: string | null) => {
  if (!role) return '';
  return role.toLowerCase().split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const errorText = (err: unknown, fallback: string) => {
  const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
  if (typeof detail === 'string') return detail;
  return fallback;
};

const SeatRow: React.FC<{ seat: RenewalSeat; ending: boolean }> = ({ seat, ending }) => {
  const holder = seat.holder;
  let who: React.ReactNode = <span className="text-[13.5px] font-semibold text-ink-dim">Open seat</span>;
  if (holder) {
    who = (
      <span className="min-w-0">
        <span className="block truncate text-[13.5px] font-semibold text-ink">{holder.name || holder.email}</span>
        <span className="block truncate text-[12px] text-ink-dim">
          {holder.name ? holder.email : ''}
          {holder.name && holder.joined_at ? ' · ' : ''}
          {holder.joined_at ? `joined ${day(holder.joined_at)}` : ''}
        </span>
      </span>
    );
  }
  let renews = `Renews ${day(seat.renews_at)}`;
  if (ending) renews = `Ends ${day(seat.renews_at)}`;
  return (
    <li className="grid min-h-[60px] grid-cols-[64px_minmax(0,1fr)] items-center gap-x-3 gap-y-1 border-b border-line-soft px-6 py-3 last:border-b-0 sm:grid-cols-[64px_minmax(0,1fr)_150px_150px]">
      <span className="text-[12.5px] font-semibold text-ink-dim">Seat {seat.number}</span>
      <span className="flex min-w-0 items-center gap-2">
        {who}
        {holder?.role && <Chip className="hidden sm:inline-flex">{roleLabel(holder.role)}</Chip>}
      </span>
      <span className="col-start-2 text-[12.5px] text-ink-dim sm:col-start-auto">
        {seat.added_at ? `Added ${day(seat.added_at)}` : 'Added before records'}
      </span>
      <span className="col-start-2 text-[12.5px] text-ink sm:col-start-auto sm:text-right">{renews}</span>
    </li>
  );
};

/* The Team plan's renewal: every seat with when it was added, the one date
 * they all renew on, what that renewal will charge, and the controls to stop
 * renewal or renew everything that needs it. */
export const RenewalPanel: React.FC<{ onChanged: () => void }> = ({ onChanged }) => {
  const [view, setView] = useState<SeatRenewal | null>(null);
  const [failed, setFailed] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  const working = useRef(false);

  const load = useCallback(async () => {
    setFailed('');
    try {
      const { data } = await billingApi.getRenewal();
      setView(data);
    } catch (err) {
      setFailed(errorText(err, 'Could not load your renewal. Refresh to try again.'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (action: () => Promise<{ data: SeatRenewal }>, done: string, fallback: string) => {
    if (working.current) return;
    working.current = true;
    setBusy(true);
    try {
      const { data } = await action();
      setView(data);
      setConfirmOff(false);
      toast.success(done);
      onChanged();
    } catch (err) {
      toast.error(errorText(err, fallback));
      load();
    } finally {
      working.current = false;
      setBusy(false);
    }
  };

  if (failed) return <ErrorNote message={failed} />;
  if (!view) {
    return (
      <Panel>
        <div className="space-y-3 px-6 py-6">
          <div className="h-4 w-40 animate-pulse rounded bg-canvas-inset" />
          <div className="h-4 w-72 animate-pulse rounded bg-canvas-inset" />
        </div>
      </Panel>
    );
  }

  const when = day(view.renews_at);
  const ending = view.needs === 'ending';
  const unpaidCents = view.unpaid.reduce((sum, inv) => sum + inv.amount_due_cents, 0);

  let title = `All ${view.seat_count} ${view.seat_count === 1 ? 'seat renews' : 'seats renew'} on ${when}`;
  let desc = 'Every seat renews together, automatically, and is charged to the card on your plan. A seat added during the month is charged only for the days left, then renews with the rest.';
  if (ending) {
    title = `Your seats end on ${when}`;
    desc = `Renewal is off. On ${when} every seat ends and your team loses access. Turn on Renew automatically to keep everyone; nothing is charged before ${when}.`;
  }
  if (view.needs === 'unpaid') {
    title = 'A renewal payment is waiting';
    desc = `Stripe could not charge ${money(unpaidCents)} for your renewal. Pay renewal charges it to the card on your plan.`;
  }
  // Only a failed renewal payment needs a button: turning renewal back on is the switch below, and charges nothing.
  let payAction: React.ReactNode = null;
  if (view.needs === 'unpaid') {
    payAction = (
      <Action
        icon={<RefreshCw size={14} />}
        onClick={() => run(billingApi.renewAll, 'Renewal paid.', 'Could not pay the renewal. Try again.')}
        disabled={busy}
        reason="Working"
      >
        Pay renewal
      </Action>
    );
  }

  const toggle = (next: boolean) => {
    if (!next) {
      setConfirmOff(true);
      return;
    }
    run(() => billingApi.setAutoRenew(true), `Your seats renew automatically again. Nothing is charged today; they renew on ${when}.`, 'Could not turn renewal on. Try again.');
  };

  const next = view.next_invoice;

  return (
    <div className="space-y-6">
      <Panel>
        <PanelHead kicker="Renewal" title={title} desc={desc} action={payAction} />
        <div className="border-t border-line-soft">
          <SettingRow
            title="Renew automatically"
            desc={view.auto_renew ? `On: all seats renew on ${when}.` : `Off: all seats end on ${when}.`}
            control={<Toggle checked={view.auto_renew} onChange={toggle} label="Renew automatically" disabled={busy} />}
          />
        </div>
      </Panel>

      {confirmOff && (
        <Modal
          size="dialog"
          context="Renewal"
          onClose={() => { if (!busy) setConfirmOff(false); }}
          footer={(
            <div className="flex flex-wrap justify-end gap-2">
              <Action variant="outline" onClick={() => setConfirmOff(false)} disabled={busy} reason="Working">
                Keep renewing
              </Action>
              <Action
                variant="danger"
                onClick={() => run(() => billingApi.setAutoRenew(false), `Renewal is off. Your seats end on ${when}.`, 'Could not turn renewal off. Try again.')}
                disabled={busy}
                reason="Working"
              >
                {busy ? 'Turning off…' : 'Turn off renewal'}
              </Action>
            </div>
          )}
        >
          <h2 className="m-0 text-[20px] font-bold leading-[28px] text-ink">Turn off renewal?</h2>
          <p className="mb-0 mt-2 text-[14px] leading-[22px] text-ink-dim">
            Nothing more is charged. On {when} every seat ends and your team loses access until you turn renewal back on.
          </p>
        </Modal>
      )}

      <Panel>
        <PanelHead
          kicker="Next renewal"
          title={next ? `${money(next.total_cents)} on ${day(next.at) || when}` : 'Nothing will be charged'}
          desc={next ? 'What your next renewal invoice holds right now. Seats added before then and any hosting share are on it.' : `Renewal is off, so there is no next invoice. Your renewal date is ${when}: turn renewal back on and every seat renews then, with nothing charged before.`}
        />
        {next && next.lines.length > 0 && (
          <ul className="m-0 list-none border-t border-line-soft p-0">
            {next.lines.map((line) => (
              <li key={`${line.kind}-${line.description}`} className="flex min-h-[48px] items-center gap-3 border-b border-line-soft px-6 py-2.5 last:border-b-0">
                <span className="min-w-0 flex-1 truncate text-[13px] text-ink">{line.description}</span>
                <span className="shrink-0 text-[13px] font-semibold text-ink">{money(line.amount_cents)}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel>
        <PanelHead
          kicker="Seats"
          title={`${view.seat_count} ${view.seat_count === 1 ? 'seat' : 'seats'} · ${money(view.seat_price_cents)} each at renewal`}
          desc={`${view.joined} in use, ${Math.max(view.seat_count - view.joined, 0)} open. Every seat shares one renewal date.`}
        />
        <ul className="m-0 list-none border-t border-line-soft p-0">
          {view.seats.map((seat) => <SeatRow key={seat.number} seat={seat} ending={ending} />)}
        </ul>
      </Panel>
    </div>
  );
};

export default RenewalPanel;
